import React, { useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiUsers, FiUserCheck, FiUserX, FiSearch, FiDownload } from 'react-icons/fi';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import API from '../api/axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import './Projects.css';
import './Users.css';

function ConfirmDialog({ title, message, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 380, textAlign: 'center', padding: '1.75rem' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: '2rem', marginBottom: '.5rem' }}>🗑️</div>
        <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem' }}>{title}</h3>
        <p style={{ color: '#64748b', fontSize: '.83rem', marginBottom: '1.25rem' }}>{message}</p>
        <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'center' }}>
          <button className="btn-secondary" style={{ flex: 1 }} onClick={onCancel}>Cancel</button>
          <button className="btn-danger" style={{ flex: 1 }} onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

const emptyForm = { name: '', email: '', role: '', isActive: true };
const editForm  = (u) => ({ name: u.name, email: u.email, password: '', role: u.role?._id || '', isActive: u.isActive });

const AVATAR_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316'];
const avatarColor = (name) => AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

export default function Users() {
  useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = () => {
    Promise.all([
      API.get('/users').then(r => setUsers(r.data)).catch(() => {}),
      API.get('/roles').then(r => setRoles(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const openModal = (user = null) => {
    setEditing(user);
    setForm(user ? editForm(user) : emptyForm);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (editing && !payload.password) delete payload.password;
      if (editing) {
        await API.put(`/users/${editing._id}`, payload);
        toast.success('User updated');
      } else {
        await API.post('/users', payload);
        toast.success('User created — login credentials sent by email');
      }
      setShowModal(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error saving user');
    }
  };


  const handleDelete = (u) => setConfirm({ user: u });
  const doDelete = async () => {
    const u = confirm.user; setConfirm(null);
    try { await API.delete(`/users/${u._id}`); toast.success('User deleted'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error deleting user'); }
  };

  const toggleStatus = async (u) => {
    try {
      await API.put(`/users/${u._id}`, { name: u.name, email: u.email, role: u.role?._id, isActive: !u.isActive });
      toast.success(`User ${!u.isActive ? 'activated' : 'deactivated'}`);
      fetchAll();
    } catch { toast.error('Failed to update status'); }
  };

  const exportPDF = () => {
    const logoUrl = `${window.location.origin}/cmpy_logo.png`;
    const rows = filtered.map((u, i) => `<tr>
      <td>${i + 1}</td><td>${u.name}</td><td>${u.email}</td>
      <td>${u.role?.name || '—'}</td>
      <td style="color:${u.isActive ? '#16a34a' : '#ef4444'}">${u.isActive ? 'Active' : 'Inactive'}</td>
      <td>${new Date(u.createdAt).toLocaleDateString('en-IN')}</td>
    </tr>`).join('');
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>Users List</title><style>
      body{font-family:Arial,sans-serif;padding:24px;font-size:12px}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:2px solid #2563eb;padding-bottom:12px;margin-bottom:16px}
      .header img{height:44px;object-fit:contain}
      table{width:100%;border-collapse:collapse}
      th{background:#2563eb;color:#fff;padding:7px 8px;text-align:left;font-size:10px}
      td{padding:5px 8px;border-bottom:1px solid #f1f5f9;font-size:11px}
      @media print{button{display:none}}
    </style></head><body>
      <div class="header">
        <div style="display:flex;align-items:center;gap:12px">
          <img src="${logoUrl}" alt="" onerror="this.style.display='none'" />
          <div><div style="font-size:17px;font-weight:700">Interior Design Co.</div><div style="font-size:11px;color:#64748b">Users List</div></div>
        </div>
        <div style="text-align:right;font-size:11px;color:#64748b">Total: ${filtered.length} users | Printed: ${new Date().toLocaleDateString('en-IN')}</div>
      </div>
      <table><thead><tr><th>#</th><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Joined</th></tr></thead>
      <tbody>${rows}</tbody></table>
      <script>window.onload=()=>{window.print();}${'<'}/script>
    </body></html>`);
    win.document.close();
  };

  const filtered = users.filter(u => {
    const matchSearch = !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
    const matchRole = !filterRole || u.role?._id === filterRole;
    const matchStatus = !filterStatus || (filterStatus === 'active' ? u.isActive : !u.isActive);
    return matchSearch && matchRole && matchStatus;
  });

  const activeCount = users.filter(u => u.isActive).length;
  const inactiveCount = users.length - activeCount;

  return (
    <Layout>
      {loading && <PageLoader text="Loading users..." />}
      <div className="page">
        <div className="page-actions">
          <div style={{ display: 'flex', gap: '.4rem' }}>
            <button className="btn-outline-sm" onClick={exportPDF}><FiDownload /> Export</button>
            <button className="btn-primary-sm" onClick={() => openModal()}><FiPlus /> New User</button>
          </div>
        </div>

        {/* Stats */}
        <div className="user-stats">
          <div className="user-stat-card">
            <div className="user-stat-icon" style={{ background: '#eff6ff', color: '#3b82f6' }}><FiUsers /></div>
            <div>
              <p className="user-stat-num">{users.length}</p>
              <p className="user-stat-label">Total Users</p>
            </div>
          </div>
          <div className="user-stat-card">
            <div className="user-stat-icon" style={{ background: '#f0fdf4', color: '#10b981' }}><FiUserCheck /></div>
            <div>
              <p className="user-stat-num">{activeCount}</p>
              <p className="user-stat-label">Active</p>
            </div>
          </div>
          <div className="user-stat-card">
            <div className="user-stat-icon" style={{ background: '#fef2f2', color: '#ef4444' }}><FiUserX /></div>
            <div>
              <p className="user-stat-num">{inactiveCount}</p>
              <p className="user-stat-label">Inactive</p>
            </div>
          </div>
          <div className="user-stat-card">
            <div className="user-stat-icon" style={{ background: '#f5f3ff', color: '#8b5cf6' }}><FiUsers /></div>
            <div>
              <p className="user-stat-num">{roles.length}</p>
              <p className="user-stat-label">Roles</p>
            </div>
          </div>
        </div>

        {/* Table card */}
        <div className="table-container">
          {/* Toolbar */}
          <div className="table-toolbar">
            <div className="search-box">
              <FiSearch />
              <input placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="al-select" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
              <option value="">All Roles</option>
              {roles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
            </select>
            <select className="al-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <span className="result-count">{filtered.length} user{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u._id}>
                  <td>
                    <div className="user-cell">
                      <div className="user-avatar-sm" style={{ background: avatarColor(u.name) }}>
                        {u.name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="user-cell-name">{u.name}</p>
                        {u.role?.isAdmin && <p className="user-cell-sub">Super Admin</p>}
                      </div>
                    </div>
                  </td>
                  <td className="text-muted">{u.email}</td>
                  <td>
                    {u.role ? (
                      <span className="role-pill" style={u.role.isAdmin
                        ? { background: '#eff6ff', color: '#3b82f6', border: '1px solid #bfdbfe' }
                        : { background: '#f5f3ff', color: '#8b5cf6', border: '1px solid #ddd6fe' }}>
                        {u.role.name}
                      </span>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', alignItems: 'flex-start' }}>
                      <span className="status-dot-badge" style={u.isActive
                        ? { background: '#f0fdf4', color: '#10b981' }
                        : { background: '#fef2f2', color: '#ef4444' }}>
                        <span className="dot" style={{ background: u.isActive ? '#10b981' : '#ef4444' }} />
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {u.mustChangePassword && (
                        <span style={{ fontSize: '0.7rem', background: '#fef9c3', color: '#854d0e', border: '1px solid #fde047', borderRadius: 5, padding: '1px 6px', fontWeight: 600 }}>
                          🔑 Pwd change pending
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-muted">{new Date(u.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td>
                    <div className="td-actions">
                      <button className="action-btn edit" onClick={() => openModal(u)} title="Edit"><FiEdit2 /></button>
                      {!u.role?.isAdmin && (
                        <button
                          className="action-btn"
                          style={{ color: u.isActive ? '#ef4444' : '#16a34a', border: `1px solid ${u.isActive ? '#fecaca' : '#bbf7d0'}` }}
                          onClick={() => toggleStatus(u)}
                          title={u.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {u.isActive ? <FiUserX /> : <FiUserCheck />}
                        </button>
                      )}
                      {!u.role?.isAdmin && (
                        <button className="action-btn delete" onClick={() => handleDelete(u)} title="Delete"><FiTrash2 /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty-msg">
                    {search ? `No users matching "${search}"` : 'No users yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {confirm && <ConfirmDialog title="Delete User" message={`Delete user "${confirm.user.name}"? This cannot be undone.`} onConfirm={doDelete} onCancel={() => setConfirm(null)} />}

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editing ? 'Edit User' : 'New User'}</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Full Name</label>
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="John Doe" />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder="john@example.com" />
                </div>
                {editing && (
                  <div className="form-group">
                    <label>Reset Password <span style={{ fontWeight: 400, color: '#94a3b8' }}>(leave blank to keep current)</span></label>
                    <input type="text" value={form.password || ''} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Enter new password" minLength={6} />
                  </div>
                )}
                {!editing && (
                  <div className="form-group">
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '0.65rem 0.9rem', fontSize: '0.82rem', color: '#166534' }}>
                      🔑 Default password <strong>Glass2026Tec</strong> will be set. User will be asked to change it on first login.
                    </div>
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label>Role</label>
                    <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} required>
                      <option value="">Select role</option>
                      {roles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                    </select>
                  </div>
                  {!(editing?.role?.isAdmin) && (
                    <div className="form-group">
                      <label>Status</label>
                      <select value={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.value === 'true' })}>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                  )}
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary">{editing ? 'Update User' : 'Create User'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
